import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { DRONE_PHOTO } from "../data/apartments";

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = ({ t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -bottom-24">
        <img
          src={DRONE_PHOTO}
          alt="Villa Navagero Erizzo e il parco visti dall'alto"
          className="w-full h-full object-cover"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#101f17]/85 via-[#101f17]/25 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#101f17]/45 via-[#101f17]/10 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-[#101f17]/55 to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-40 w-full">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="text-xs uppercase tracking-[0.35em] text-[#C29B62] mb-6"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#F7F5F0] leading-[1.05] max-w-3xl">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="hero-mask-line">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
              >
                {i === 1 ? <em className="text-[#C29B62] not-italic font-medium">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.8 }}
          className="mt-8 max-w-xl text-base text-[#F7F5F0]/80 font-light"
          data-testid="hero-subtitle"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link
            data-testid="hero-book-button"
            to="/prenota"
            className="bg-[#C29B62] text-[#101f17] text-xs uppercase tracking-[0.25em] px-8 py-4 hover:bg-[#F7F5F0] transition-colors"
          >
            {t.hero.cta}
          </Link>
          <Link
            data-testid="hero-discover-button"
            to="/appartamenti"
            className="border border-[#F7F5F0]/40 text-[#F7F5F0] text-xs uppercase tracking-[0.25em] px-8 py-4 hover:border-[#C29B62] hover:text-[#C29B62] transition-colors"
          >
            {t.hero.cta2}
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
